// AI Generated Test
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PolicyHelperService } from '@client/app/blocks/helpers/policy-helper/policy.helper.service';
import { SessionService } from '@monsido/core/session/session.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';

@Component({
    selector: 'policy-information',
    standalone: false,
    template: `
        <div class="policy-information">
            <span class="policy-title text-bold pr-1">{{ policy.name }}</span>
            <br/>
            <span class="text-secondary-4 pr-1">{{ getSubjectTranslation() }}</span>
            <div class="row">
                <div class="col-48 font-size-0-9 text-grey mb-1">
                    <span class="cursor-pointer inner-r-smurf"
                          [ngClass]="getIconClass('INFO')"
                          *ngIf="popoverOpen"
                          [ngbTooltip]="'Policy info' | translate"
                          [ngbPopover]="policyInfoTemplate"
                          placement="bottom-left"
                          popoverClass="text-ellipsis popover-bs3"
                          role="button"
                          [attr.aria-label]="'Show policy info' | translate"
                          [autoClose]="'outside'"
                          [animation]="false">
                    </span>
                    <span class="cursor-pointer inner-r-smurf"
                          [ngClass]="getIconClass('INFO')"
                          *ngIf="!popoverOpen"
                          [ngbTooltip]="'Policy info' | translate"
                          [ngbPopover]="policyInfoTemplate"
                          placement="bottom-left"
                          popoverClass="text-ellipsis popover-bs3"
                          role="button"
                          [attr.aria-label]="'Show policy info' | translate"
                          [autoClose]="'outside'"
                          [animation]="false">
                    </span>
                    <span class="cursor-pointer inner-r-smurf"
                          [ngClass]="getIconClass('CLOCK')"
                          *ngIf="policy.scheduled"
                          [ngbTooltip]="'Scheduled' | translate">
                    </span>
                    <span class="cursor-pointer text-danger inner-r-smurf"
                          [ngClass]="getIconClass('BAN')"
                          *ngIf="policy.mode === 'violation'"
                          [ngbTooltip]="'Unwanted content' | translate">
                    </span>
                    <span class="cursor-pointer inner-r-smurf"
                          [ngClass]="getIconClass('SYNC')"
                          *ngIf="isDomainActive()"
                          [ngbTooltip]="getLastRunMessage()">
                    </span>
                    <span class="text-grey inner-r-smurf"
                          [ngClass]="getIconClass('COMMENT')"
                          *ngIf="policy.note"
                          [ngbTooltip]="'Notes' | translate"
                          [ngbPopover]="notesTemplate"
                          placement="bottom-left"
                          popoverClass="popover-bs3 monsido-card text-ellipsis"
                          role="button"
                          [attr.aria-label]="'Toggle notes' | translate"
                          [autoClose]="'outside'"
                          [animation]="false"
                    >
                    </span>
                    <span *ngIf="policy.created_by_ai"
                          [ngbTooltip]="'Created with AI policy assistant' | translate">
                        <img class="text-v-text-top" src="/images/policy/sparkle.svg" [attr.alt]="'Sparkle icon' | translate">
                    </span>
                </div>
            </div>
        </div>

        <!-- Policy info popover template -->
        <ng-template #policyInfoTemplate>
            <div id="policy-info-custom-popover-template" class="policy-info-custom-popover-template">
                <h3 class="popover-title">
                    <span></span>
                    <span translate>Policy info</span>
                </h3>
                <div class="popover-content">
                    <table class="table table-borderless bg-transparent outer-b-none">
                        <tbody>
                        <tr *ngIf="isDomainActive()">
                            <td>
                                <span [ngClass]="getIconClass('SYNC')"></span>
                                <span translate>Last run:</span>
                            </td>
                            <td>
                                <span *ngIf="isDomainActive()" class="text-black">
                                    {{getDomainTargetScanDate()}}
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span [ngClass]="getIconClass('INFO')"></span>
                                <span translate>Created:</span>
                            </td>
                            <td class="text-black text-right">
                                {{policy.created_at | userDate:'lll'}}
                            </td>
                        </tr>
                        <tr *ngIf="policy.mode === 'violation'">
                            <td colspan="2">
                                <span [ngClass]="getIconClass('BAN')"></span>
                                <span translate>Unwanted content</span>
                            </td>
                        </tr>
                        <tr *ngIf="policy.scheduled">
                            <td colspan="2">
                                <span [ngClass]="getIconClass('CLOCK')"></span>
                                <span translate>Scheduled</span>
                            </td>
                        </tr>
                        <ng-container *ngFor="let target of policy.targets; trackBy: trackByIndex">
                            <tr *ngIf="!isDomainActive()">
                                <td>
                                    {{target.title}}
                                </td>
                                <td class="text-black text-right">
                                    <span *ngIf="target.latest_run_at === null" translate>
                                        Never scanned
                                    </span>
                                    <span *ngIf="target.latest_run_at !== null">
                                        {{target.latest_run_at | userDate: 'lll'}}
                                    </span>
                                </td>
                            </tr>
                        </ng-container>
                        </tbody>
                    </table>
                </div>
            </div>
        </ng-template>

        <!-- Notes popover template -->
        <ng-template #notesTemplate>
            <div class="d-flex align-items-center popover-title">
                <div class="pl-2">
                    <span [ngClass]="getIconClass('COMMENT')" class="text-grey"></span>
                </div>
                <div>
                    <h3 class="font-weight-400 ml-1 text-black" translate>Policy note</h3>
                </div>
            </div>
            <div class="popover-content text-grey" [innerHTML]="policy.note"></div>
        </ng-template>
    `,
})
export class PolicyInformationComponent implements OnInit {
    @Input() policy: any;

    popoverOpen: boolean = false;
    notesPopupOpen: boolean = false;

    constructor (
        public translateService: TranslateService,
        private policyHelperService: PolicyHelperService,
        private sessionService: SessionService,
        private pipeService: PipeService,
    ) {}

    ngOnInit (): void {
        this.popoverOpen = false;
        this.notesPopupOpen = false;
    }

    getIconClass (iconType: string): string {
        return this.pipeService.get('monIcons')(['GLOBAL_POLICY_OVERVIEW', 'POLICY_INFORMATION', iconType]);
    }

    getSubjectTranslation (): string {
        switch (this.policy.subject) {
            case 'page':
                return this.translateService.getString('Search in: Everything');
            case 'page_html':
                return this.translateService.getString('Search in: Only HTML pages');
            case 'page_document':
                return this.translateService.getString('Search in: Documents');
            case 'link':
                return this.translateService.getString('Search in: Links');
            default:
                return '';
        }
    }

    popoverClick (): void {
        this.popoverOpen = !this.popoverOpen;
    }

    outsideClick (): void {
        this.popoverOpen = false;
    }

    isDomainActive (): boolean {
        return this.sessionService.domain !== null;
    }

    getDomainTargetScanDate (): string {
        const target = this.policyHelperService.getTargetDomain(this.policy.targets);
        if (target && target.latest_run_at) {
            return this.pipeService.get('userDate')(target.latest_run_at, 'lll');
        } else {
            return this.translateService.getString('Never');
        }
    }

    getLastRunMessage (): string {
        return this.translateService.getString('Last run [[date]]', {
            date: this.getDomainTargetScanDate(),
        });
    }

    trackByIndex (index: number, item: any): number {
        return index;
    }
}
